import React, { useState } from 'react';
import { ArrowLeft, Send } from 'lucide-react';
import { submitContactLead } from '../services/leadsApi';
import ConsentCheckbox from './ConsentCheckbox';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { validatePhone, formatPhoneInput } from '../utils/phoneValidation';
import FieldError from './FieldError';
import PhoneValid from './PhoneValid';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [formRef, formVisible] = useScrollAnimation({ threshold: 0.1 });

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue = name === 'phone' ? formatPhoneInput(value) : value;
    if (name === 'phone' && phoneError) setPhoneError('');
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) return;
    const phoneErr = validatePhone(formData.phone);
    if (phoneErr) { setPhoneError(phoneErr); return; }
    setIsSubmitting(true);
    try {
      await submitContactLead(formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Error:', error);
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-white py-24 relative">
      {/* Decorative Elements */}
      <div className="absolute top-10 right-10 w-40 h-40 bg-[#C5366A]/5 rounded-full blur-2xl" />
      <div className="absolute bottom-10 left-10 w-60 h-60 bg-[#3D4B8C]/5 rounded-full blur-2xl" />

      <div className="max-w-[1400px] mx-auto px-[7.6923%] relative z-10">
        <div className="max-w-2xl mx-auto" dir="rtl">
          {/* Section Header */}
          <div 
            ref={headerRef}
            className={`text-center mb-12 transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-[#C5366A] text-base font-medium mb-4 tracking-wide">
              צור קשר
            </p>
            <h2 className="text-[#2D2D2D] text-3xl md:text-4xl lg:text-5xl font-semibold mb-6">
              בואו נדבר
            </h2>
            <p className="text-[#6B6B6B] text-lg">
              מלאו את הטופס ונחזור אליכם בהקדם
            </p>
          </div>

          {/* Form */}
          {!submitted ? (
            <form 
              ref={formRef}
              onSubmit={handleSubmit} 
              className={`space-y-6 bg-[#F8EEF2] p-8 rounded-xl border border-[#C5366A]/10 transition-all duration-700 delay-150 ${
                formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className="block text-[#2D2D2D] text-sm mb-2 font-medium">שם מלא</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-white border border-[#C5366A]/20 text-[#2D2D2D] px-4 py-3 rounded-lg focus:outline-none focus:border-[#C5366A] transition-colors text-base"
                    placeholder="השם שלך"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-[#2D2D2D] text-sm mb-2 font-medium">טלפון</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    pattern="[\d\-\+\s\(\)]{7,15}"
                    title="נא להזין מספר טלפון תקין"
                    maxLength={14}
                    aria-invalid={!!phoneError}
                    className="w-full bg-white border border-[#C5366A]/20 text-[#2D2D2D] px-4 py-3 rounded-lg focus:outline-none focus:border-[#C5366A] transition-colors text-base"
                    placeholder="050-0000000"
                    data-testid="contact-phone-input"
                  />
                  <FieldError>{phoneError}</FieldError>
                  <PhoneValid phone={formData.phone} />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-[#2D2D2D] text-sm mb-2 font-medium">אימייל</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-white border border-[#C5366A]/20 text-[#2D2D2D] px-4 py-3 rounded-lg focus:outline-none focus:border-[#C5366A] transition-colors text-base"
                  placeholder="email@example.com"
                />
              </div>

              {/* Subject */}
              <div>
                <label className="block text-[#2D2D2D] text-sm mb-2 font-medium">נושא פנייה</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full bg-white border border-[#C5366A]/20 text-[#2D2D2D] px-4 py-3 rounded-lg focus:outline-none focus:border-[#C5366A] transition-colors text-base"
                >
                  <option value="">בחר נושא</option>
                  <option value="ai-agency">הקמת סוכנות AI</option>
                  <option value="partner-track">נבחרת השותפים</option>
                  <option value="opm">שירותי OPM לחברות</option>
                  <option value="other">אחר</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label className="block text-[#2D2D2D] text-sm mb-2 font-medium">הודעה</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full bg-white border border-[#C5366A]/20 text-[#2D2D2D] px-4 py-3 rounded-lg focus:outline-none focus:border-[#C5366A] transition-colors text-base resize-none"
                  placeholder="ספר לנו קצת על עצמך..."
                />
              </div>

              {/* Consent Checkbox */}
              <ConsentCheckbox checked={consent} onChange={setConsent} />

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !consent}
                className="w-full flex items-center justify-center gap-3 bg-[#C5366A] text-white px-8 py-4 text-lg font-medium rounded-lg hover:bg-[#A12D56] transition-all duration-400 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#C5366A]/30"
              >
                {isSubmitting ? (
                  <span>שולח...</span>
                ) : (
                  <>
                    <span>שלח פנייה</span>
                    <Send className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          ) : (
            /* Success Message */
            <div className="text-center py-12 bg-[#F8EEF2] border border-[#C5366A]/20 rounded-xl">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#C5366A] rounded-full">
                <ArrowLeft className="w-8 h-8 text-white rotate-[135deg]" />
              </div>
              <h3 className="text-[#2D2D2D] text-2xl font-semibold mb-4">
                הפנייה התקבלה בהצלחה!
              </h3>
              <p className="text-[#6B6B6B] text-lg">
                נחזור אליך תוך 24 שעות עסקים
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
